import {
  useGetEOBCount,
  useGetSiteClaimTariffStatusesCount,
  useListEobs,
  useListProvidersBySite,
  useListRemittanceBySite,
  useListSites,
  useListStatus,
  prefetchPaginateClaimsBySite,
  usePaginateClaimsBySite,
} from 'api/resource';
import { useQueryClient } from '@tanstack/react-query';
import { useListPayors } from 'api/resource/payor';
import { useCombineStates } from 'hooks/useCombineStates';
import { useRemoteFeatureFlag } from 'hooks/useRemoteFeatureFlag';
import { useEffect, useState } from 'react';
import { useCurrentSiteStore } from 'store/SiteStore';
import { useClaimPaginationStore } from 'store/ClaimPaginationStore';

export const useAdminClaimManagementData = () => {
  const queryClient = useQueryClient();
  const { id: siteID, setCurrentSite } = useCurrentSiteStore();
  const { pagination, setPagination } = useClaimPaginationStore();
  const isNextPagePrefetchEnabled = useRemoteFeatureFlag(
    'claim_table_next_page_prefetch'
  );

  const sitesResult = useListSites({ isAdmin: true });
  const fallbackSite = sitesResult.data?.[0];
  const resolvedSiteID = siteID || fallbackSite?.id || '';

  useEffect(() => {
    if (!siteID && fallbackSite) {
      setCurrentSite(fallbackSite);
    }
  }, [fallbackSite, setCurrentSite, siteID]);

  const paginatedClaimsResult = usePaginateClaimsBySite(
    resolvedSiteID,
    pagination.pageIndex,
    pagination.pageSize,
    pagination.columnFilters,
    pagination.globalFilter,
    pagination.sorting
  );

  useEffect(() => {
    if (!isNextPagePrefetchEnabled || !resolvedSiteID) return;
    void prefetchPaginateClaimsBySite(
      queryClient,
      resolvedSiteID,
      pagination.pageIndex + 1,
      pagination.pageSize,
      pagination.columnFilters,
      pagination.globalFilter,
      pagination.sorting
    );
  }, [
    isNextPagePrefetchEnabled,
    queryClient,
    resolvedSiteID,
    pagination.pageIndex,
    pagination.pageSize,
    pagination.columnFilters,
    pagination.globalFilter,
    pagination.sorting,
  ]);

  const claimStatusesCountResult = useGetSiteClaimTariffStatusesCount(
    resolvedSiteID,
    pagination.columnFilters,
    pagination.globalFilter
  );
  const [eobLookupsRequested, setEobLookupsRequested] = useState(false);
  const shouldLoadEobLookups =
    eobLookupsRequested ||
    (pagination.columnFilters ?? []).some(
      (filter) => filter.id === 'debits.eobs'
    );
  const eobsResult = useListEobs({ enabled: shouldLoadEobLookups });
  const eobCountsResult = useGetEOBCount({ enabled: eobLookupsRequested });

  const providersResult = useListProvidersBySite({ siteID: resolvedSiteID });
  const remittanceResult = useListRemittanceBySite({ siteID: resolvedSiteID });
  const statusesResult = useListStatus({});
  const payorsResult = useListPayors();

  const { isLoading, isError, isFetching, errorMessage } = useCombineStates([
    paginatedClaimsResult,
    claimStatusesCountResult,
    providersResult,
    sitesResult,
    statusesResult,
    payorsResult,
    remittanceResult,
  ]);

  return {
    claims: {
      data: paginatedClaimsResult.data?.data || [],
      total: paginatedClaimsResult.data?.total || 0,
      nextPage: paginatedClaimsResult.data?.nextPage || 0,
      invalidData: paginatedClaimsResult.data?.invalidData || [],
      totalAmountSubmitted:
        paginatedClaimsResult.data?.totalAmountSubmitted || 0,
      totalAmountPaid: paginatedClaimsResult.data?.totalAmountPaid || 0,
      totalAmountOutstanding:
        paginatedClaimsResult.data?.totalAmountOutstanding || 0,
    },
    claimStatuses: claimStatusesCountResult.data || {},
    providers: providersResult.data || [],
    sites: sitesResult.data || [],
    status: statusesResult.data || [],
    payors: payorsResult.data || [],
    remittances: remittanceResult.data || [],
    eobs: eobsResult.data || [],
    eobCounts: eobCountsResult.data || {},
    isLoading,
    isError: isError || eobsResult.isError || eobCountsResult.isError,
    isFetching,
    errorMessage:
      errorMessage ||
      eobsResult.error?.message ||
      eobCountsResult.error?.message ||
      '',
    pagination,
    setPagination,
    refetchClaims: paginatedClaimsResult.refetch,
    refetchStatusesCount: claimStatusesCountResult.refetch,
    loadEobLookups: () => setEobLookupsRequested(true),
  };
};
